import {
  GoogleLoginResponse,
} from 'react-google-login';
import { 
  useDispatch,
  useSelector,
} from 'react-redux';
import { authSlice } from '../../slices/auth';
import { usePostAuthenticationUpgradeMutation } from '../../slices/auth/v6';
import { usePostGoogleSocialAuthenticationMutation } from '../../slices/auth/socialAuthApi';
import { tryCatch } from '../../utils/tryCatch';
import { 
  DeviceType,
  Auth 
} from '../../constants'

/**
 * useGoogleLogin is a hook composed of `usePostGoogleSocialAuthenticationMutation`
 * and `usePostAuthenticationUpgrade`
 */

export const useGoogleLogin = () => {

  const dispatch = useDispatch();

  const [
    executePostGoogleSocialAuthentication,
    {
      isLoading: googleAuthenticationIsLoading,
      isSuccess: googleAuthenticationIsSuccess,
      isError: googleAuthenticationIsError,
      data: googleAuthenticationResponse,
      error: googleAuthenticationError,
    }
  ] = usePostGoogleSocialAuthenticationMutation();

  const [
    executePostAuthenticationUpgrade,
    {
      isLoading: isPostAuthenticationUpgradeLoading,
      isSuccess: isPostAuthenticationUpgradeSuccess,
      isError: postAuthenticationUpgradeError,
      error: postAuthenticationUpgradeErrorMessage,
    }
  ] = usePostAuthenticationUpgradeMutation();

  const googleIdToken = useSelector(authSlice.selectors.getGoogleIdToken);

  const execute = async (googleLoginResponse: GoogleLoginResponse) => {
    //TODO rename back to id_token downstream to epic
    const idToken = googleLoginResponse.getAuthResponse().id_token;
    dispatch(authSlice.actions.setGoogleIdToken(idToken));
    const [response] = await tryCatch(() => (
      executePostGoogleSocialAuthentication({
        accessToken: idToken,
      }).unwrap())
    );

    if (response.id && response.token) {
      /* 
        Log the user straight into the app
        if they are not a new user and their account has been
        previously linked( the conditions for newAccount:false to be returned)
      */
      if (!response.newAccount) {
        return executePostAuthenticationUpgrade({
          deviceType: DeviceType.Web,
          id: response.id,
          token: response.token,
          userRoleType: Auth.RoleType.Owner,
        });
      }
      return response;
    }
  };

  return {
    execute,
    isLoading: googleAuthenticationIsLoading || isPostAuthenticationUpgradeLoading,
    isGoogleLoginSuccess: googleAuthenticationIsSuccess,
    isSuccess: isPostAuthenticationUpgradeSuccess,
    error: googleAuthenticationIsError || postAuthenticationUpgradeError,
    errorMessage: googleAuthenticationError || postAuthenticationUpgradeErrorMessage,
    googleAuthenticationResponse,
    googleIdToken,
  };
};
